// =============================================================================
// MeuDriver — M1: Hook de Ganhos do Instrutor
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { startOfMonth, endOfMonth, subMonths, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EarningsSummary {
  total_gross: number
  total_net: number
  platform_fees: number
  completed_lessons: number
  cancelled_lessons: number
  avg_per_lesson: number
  days_with_lessons: number
}

export interface MonthlyEarning {
  month: string       // "YYYY-MM"
  label: string       // "Jan/25"
  net: number
  gross: number
  lessons: number
}

export interface TopStudent {
  student_id: string
  first_name: string
  total_net: number
  lesson_count: number
}

// ---------------------------------------------------------------------------
// Resolve instructor_id from profile_id
// ---------------------------------------------------------------------------

async function resolveInstructorId(profileId: string): Promise<string | null> {
  const { data } = await supabase
    .from('instructors')
    .select('id')
    .eq('profile_id', profileId)
    .maybeSingle()
  return data?.id ?? null
}

// ---------------------------------------------------------------------------
// useEarningsSummary
// ---------------------------------------------------------------------------

export function useEarningsSummary(startDate: Date, endDate: Date) {
  const { user } = useAuth()

  return useQuery<EarningsSummary | null>({
    queryKey: ['earnings-summary', user?.id, startDate.toISOString(), endDate.toISOString()],
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!user?.id) return null

      const instructorId = await resolveInstructorId(user.id)
      if (!instructorId) return null

      const { data, error } = await supabase.rpc('get_instructor_earnings_summary', {
        p_instructor_id: instructorId,
        p_start_date: format(startDate, 'yyyy-MM-dd'),
        p_end_date: format(endDate, 'yyyy-MM-dd'),
      })

      if (error) throw error

      // RPC retorna JSONB; cast seguro
      const raw = data as Record<string, number> | null
      if (!raw) return null

      return {
        total_gross: Number(raw.total_gross ?? 0),
        total_net: Number(raw.total_net ?? 0),
        platform_fees: Number(raw.platform_fees ?? 0),
        completed_lessons: Number(raw.completed_lessons ?? 0),
        cancelled_lessons: Number(raw.cancelled_lessons ?? 0),
        avg_per_lesson: Number(raw.avg_per_lesson ?? 0),
        days_with_lessons: Number(raw.days_with_lessons ?? 0),
      }
    },
  })
}

// ---------------------------------------------------------------------------
// useEarningsHistory — array mensal dos últimos N meses
// ---------------------------------------------------------------------------

export function useEarningsHistory(months = 6) {
  const { user } = useAuth()

  return useQuery<MonthlyEarning[]>({
    queryKey: ['earnings-history', user?.id, months],
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!user?.id) return []

      const instructorId = await resolveInstructorId(user.id)
      if (!instructorId) return []

      const now = new Date()
      const results: MonthlyEarning[] = []

      // Busca todos os bookings concluídos de uma vez, filtrando no cliente
      const rangeStart = startOfMonth(subMonths(now, months - 1))
      const rangeEnd = endOfMonth(now)

      const { data } = await supabase
        .from('bookings')
        .select('scheduled_at, total_price, instructor_payout, status')
        .eq('instructor_id', instructorId)
        .eq('status', 'completed')
        .gte('scheduled_at', rangeStart.toISOString())
        .lte('scheduled_at', rangeEnd.toISOString())

      const bookings = data ?? []

      for (let i = months - 1; i >= 0; i--) {
        const monthDate = subMonths(now, i)
        const mStart = startOfMonth(monthDate).toISOString()
        const mEnd = endOfMonth(monthDate).toISOString()
        const monthKey = format(monthDate, 'yyyy-MM')
        const label = format(monthDate, "MMM/yy", { locale: ptBR })

        const monthBookings = bookings.filter(
          (b) => b.scheduled_at >= mStart && b.scheduled_at <= mEnd,
        )

        results.push({
          month: monthKey,
          label,
          net: monthBookings.reduce((s, b) => s + (b.instructor_payout ?? 0), 0),
          gross: monthBookings.reduce((s, b) => s + (b.total_price ?? 0), 0),
          lessons: monthBookings.length,
        })
      }

      return results
    },
  })
}

// ---------------------------------------------------------------------------
// useEarningsProjection — média dos últimos 3 meses
// ---------------------------------------------------------------------------

export function useEarningsProjection() {
  const { data: history, ...rest } = useEarningsHistory(3)

  const projection =
    history && history.length > 0
      ? history.reduce((s, m) => s + m.net, 0) / history.length
      : 0

  return { projection, ...rest }
}

// ---------------------------------------------------------------------------
// useTopStudents — top N alunos por receita líquida
// ---------------------------------------------------------------------------

export function useTopStudents(limit = 5) {
  const { user } = useAuth()

  return useQuery<TopStudent[]>({
    queryKey: ['top-students', user?.id, limit],
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      if (!user?.id) return []

      const instructorId = await resolveInstructorId(user.id)
      if (!instructorId) return []

      // Busca bookings concluídos com nome do aluno
      const { data } = await supabase
        .from('bookings')
        .select(`
          student_id,
          instructor_payout,
          student:profiles!bookings_student_id_fkey(full_name)
        `)
        .eq('instructor_id', instructorId)
        .eq('status', 'completed')

      if (!data) return []

      // Agrupa por aluno
      const map = new Map<string, { first_name: string; total_net: number; lesson_count: number }>()

      for (const b of data) {
        const studentProfile = b.student as { full_name: string | null } | null
        const name = studentProfile?.full_name ?? ''
        // Privacidade: apenas primeiro nome
        const firstName = name.split(' ')[0] ?? 'Aluno'
        const existing = map.get(b.student_id)
        if (existing) {
          existing.total_net += b.instructor_payout ?? 0
          existing.lesson_count += 1
        } else {
          map.set(b.student_id, {
            first_name: firstName,
            total_net: b.instructor_payout ?? 0,
            lesson_count: 1,
          })
        }
      }

      return Array.from(map.entries())
        .map(([student_id, v]) => ({ student_id, ...v }))
        .sort((a, b) => b.total_net - a.total_net)
        .slice(0, limit)
    },
  })
}
